import balanceJson from "./tables/raid/balance.json";
import rosterJson from "./tables/raid/roster.json";
import bossesJson from "./tables/raid/bosses.json";
import assaultJson from "./tables/raid/assault.json";
import profilesJson from "./tables/raid/ai-profiles.json";
import gambitsJson from "./tables/raid/ai-gambits.json";
import behaviorKitsJson from "./tables/raid/ai-behavior-kits.json";
import commanderJson from "./tables/raid/commander.json";
import barksJson from "./tables/raid/barks.json";
import aggroJson from "./tables/raid/aggro.json";
import threatVolumeJson from "./tables/raid/threat-volume.json";
import enemiesJson from "./tables/raid/enemies.json";
import viewJson from "./tables/presentation/raid-view.json";

export type RaidBalance = typeof balanceJson;
export type RaidRosterTable = typeof rosterJson;
export type RaidBossesTable = typeof bossesJson;
export type RaidAssaultTable = typeof assaultJson;
export type RaidAiProfiles = typeof profilesJson;
export type RaidAiGambits = typeof gambitsJson;
export type RaidAiBehaviorKits = typeof behaviorKitsJson;
export type RaidCommanderTable = typeof commanderJson;
export type RaidBarksTable = typeof barksJson;
export type RaidAggroTable = typeof aggroJson;
export type RaidThreatVolumeTable = typeof threatVolumeJson;
export type RaidEnemiesTable = typeof enemiesJson;
export type RaidViewTable = typeof viewJson;

export type RaidClassId = "tank" | "dps" | "healer";

/** Class signature active (table-driven; auto-cast when CD ready). */
export type RaidActiveId = "shield_bash" | "marked_shot" | "heal";

export type RaidActiveKit = {
  id: RaidActiveId;
  nameKey: string;
  cooldown: number;
  damageMul?: number;
  toughnessMul?: number;
  amount?: number;
};

/** Temperament axes — tradeoff squad surface (0..100 each). Instinct is separate. */
export type RaidAxisId = "grit" | "measure" | "bond" | "selfProtect";
export type RaidDisposition = Record<RaidAxisId, number>;

/** Gambit row: [conditionChip(:arg), behaviorChip] — ordered T3 rule slots. */
export type RaidGambitRow = readonly [string, string];

export const raidCatalog = {
  balance: balanceJson as RaidBalance,
  roster: rosterJson as RaidRosterTable,
  bosses: bossesJson as RaidBossesTable,
  assault: assaultJson as RaidAssaultTable,
  profiles: profilesJson as RaidAiProfiles,
  gambits: gambitsJson as RaidAiGambits,
  behaviorKits: behaviorKitsJson as RaidAiBehaviorKits,
  commander: commanderJson as RaidCommanderTable,
  barks: barksJson as RaidBarksTable,
  aggro: aggroJson as RaidAggroTable,
  threatVolume: threatVolumeJson as RaidThreatVolumeTable,
  enemies: enemiesJson as RaidEnemiesTable,
  view: viewJson as RaidViewTable,
} as const;

export function hexToColor(hex: string): number {
  const s = hex.replace("#", "").slice(0, 6);
  return Number.parseInt(s, 16);
}

/** Session handoff: roster → battle. */
let pendingPartyIds: string[] | null = null;

export function setRaidParty(ids: readonly string[]): void {
  pendingPartyIds = [...ids];
}

export function takeRaidParty(): string[] {
  const ids =
    pendingPartyIds ??
    ([...raidCatalog.roster.defaultParty] as string[]);
  pendingPartyIds = null;
  return ids;
}

export function peekRaidParty(): readonly string[] | null {
  return pendingPartyIds;
}

/** Live battle boot: waves (assault) vs direct Colossus. Default assault. */
export type RaidEncounterStartMode = "assault" | "boss";

let pendingEncounterStart: RaidEncounterStartMode | null = null;

export function setRaidEncounterStart(mode: RaidEncounterStartMode): void {
  pendingEncounterStart = mode;
}

export function takeRaidEncounterStart(): RaidEncounterStartMode {
  const mode = pendingEncounterStart ?? "assault";
  pendingEncounterStart = null;
  return mode;
}

export function peekRaidEncounterStart(): RaidEncounterStartMode | null {
  return pendingEncounterStart;
}

/**
 * Player AI growth save (session layer): per-defId axis tuning (±tuningCap)
 * and gambit rule overrides. Persistence format = this exact shape.
 * `absolute` is Sim Lab only — forces effective 0..100 (bypasses base+tuning).
 */
export type RaidAiSave = {
  tuning?: Record<
    string,
    Partial<RaidDisposition> & { instinct?: number }
  >;
  rules?: Record<string, RaidGambitRow[]>;
  absolute?: Record<
    string,
    Partial<RaidDisposition> & { instinct?: number }
  >;
};

let aiSave: RaidAiSave = {};

/** Legacy Sim saves may still carry temperament `focus`; fold into bond. */
function migrateFocusIntoBondSave(save: RaidAiSave): RaidAiSave {
  const fold = (
    row: (Partial<RaidDisposition> & { instinct?: number }) | undefined,
  ): (Partial<RaidDisposition> & { instinct?: number }) | undefined => {
    if (!row) return row;
    const legacy = row as Partial<RaidDisposition> & {
      instinct?: number;
      focus?: number;
    };
    if (legacy.focus == null || !Number.isFinite(legacy.focus)) {
      const { focus: _f, ...rest } = legacy;
      return rest;
    }
    const focus = legacy.focus;
    const { focus: _drop, ...rest } = legacy;
    // Old focus100 (personal sticky) ≡ bond0; focus0 ≡ bond100.
    const fromFocus = 100 - focus;
    const bond =
      rest.bond != null && Number.isFinite(rest.bond)
        ? Math.max(
            0,
            Math.min(100, rest.bond - 0.5 * (focus - 50)),
          )
        : fromFocus;
    return { ...rest, bond };
  };
  const mapRec = (
    rec:
      | Record<string, Partial<RaidDisposition> & { instinct?: number }>
      | undefined,
  ) => {
    if (!rec) return rec;
    const out: Record<string, Partial<RaidDisposition> & { instinct?: number }> =
      {};
    for (const [k, v] of Object.entries(rec)) {
      out[k] = fold(v) ?? {};
    }
    return out;
  };
  return {
    ...save,
    tuning: mapRec(save.tuning),
    absolute: mapRec(save.absolute),
  };
}

export function setRaidAiSave(save: RaidAiSave): void {
  aiSave = migrateFocusIntoBondSave(save);
}

export function raidAiTuningFor(defId: string): Partial<RaidDisposition> {
  const row = aiSave.tuning?.[defId];
  if (!row) return {};
  const { instinct: _i, ...axes } = row;
  return axes;
}

export function raidAiInstinctTuningFor(defId: string): number {
  return aiSave.tuning?.[defId]?.instinct ?? 0;
}

export function raidAiAbsoluteFor(
  defId: string,
): (Partial<RaidDisposition> & { instinct?: number }) | null {
  return aiSave.absolute?.[defId] ?? null;
}

/** Instinct 0..100 — reaction speed (not temperament). */
export function resolveInstinct(
  defId: string,
  classId: RaidClassId,
): number {
  const abs = raidAiAbsoluteFor(defId)?.instinct;
  if (abs != null && Number.isFinite(abs)) {
    return Math.max(0, Math.min(100, abs));
  }
  const p = raidCatalog.profiles as {
    instinct?: {
      classBase?: Record<string, number>;
      byDefId?: Record<string, number>;
    };
    tuningCap?: number;
  };
  const base = p.instinct?.classBase?.[classId] ?? 50;
  const off = p.instinct?.byDefId?.[defId] ?? 0;
  const cap = p.tuningCap ?? 25;
  const t = Math.max(-cap, Math.min(cap, raidAiInstinctTuningFor(defId)));
  return Math.max(0, Math.min(100, base + off + t));
}

/** Authored per-hero disposition quirk + axis offsets (classBase stays role floor). */
export type RaidHeroProfile = {
  quirkId: string;
  offsets: Partial<RaidDisposition>;
};

export function raidHeroProfileFor(defId: string): RaidHeroProfile | null {
  const row = (
    raidCatalog.profiles as { byDefId?: Record<string, RaidHeroProfile> }
  ).byDefId?.[defId];
  return row ?? null;
}

export function raidGambitRulesFor(
  defId: string,
  classId: RaidClassId,
): readonly RaidGambitRow[] {
  const custom = aiSave.rules?.[defId];
  if (custom && custom.length) return custom;
  const byHero = (
    raidCatalog.gambits as { byDefId?: Record<string, string[][]> }
  ).byDefId?.[defId];
  if (byHero && byHero.length) {
    return byHero as unknown as RaidGambitRow[];
  }
  const rows = raidCatalog.gambits.defaultRules[classId] ?? [];
  // JSON rows are string[]; runtime shape is [condition, behavior].
  return rows as unknown as RaidGambitRow[];
}

/** Party-class active kit (adds have none). */
export function raidActiveFor(classId: RaidClassId): RaidActiveKit | null {
  const row = (
    raidCatalog.balance as { actives?: Partial<Record<RaidClassId, RaidActiveKit>> }
  ).actives?.[classId];
  if (!row || !(row.cooldown > 0)) return null;
  return row;
}
